import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PhaseTierDefault {
  id: string;
  phase_key: string;
  tier: 'good' | 'better' | 'best';
  product_id: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  product?: {
    id: string;
    name: string;
    brand: string | null;
    image_url: string | null;
    category: string;
    unit: string | null;
    customer_description: string | null;
  };
}

// Fetch all tier defaults with product info
export function usePhaseTierDefaults() {
  return useQuery({
    queryKey: ['phase-tier-defaults'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('phase_tier_defaults')
        .select(`
          *,
          product:products(id, name, brand, image_url, category)
        `)
        .order('phase_key');

      if (error) throw error;
      return data as PhaseTierDefault[];
    },
  });
}

// Get defaults for a specific phase
export function usePhaseTierDefaultsForPhase(phaseKey: string) {
  return useQuery({
    queryKey: ['phase-tier-defaults', phaseKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('phase_tier_defaults')
        .select(`
          *,
          product:products(id, name, brand, image_url, category, unit, customer_description)
        `)
        .eq('phase_key', phaseKey)
        .order('tier');

      if (error) throw error;
      return data as PhaseTierDefault[];
    },
  });
}

// Set/update tier default
export function useSetPhaseTierDefault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      phase_key,
      tier,
      product_id,
      is_default = false,
    }: {
      phase_key: string;
      tier: 'good' | 'better' | 'best';
      product_id: string;
      is_default?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('phase_tier_defaults')
        .upsert(
          { phase_key, tier, product_id, is_default, updated_at: new Date().toISOString() },
          { onConflict: 'phase_key,tier' }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phase-tier-defaults'] });
      toast.success('Tier default updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update tier default', { description: error.message });
    },
  });
}

// Set which tier is the default for new estimates
export function useSetDefaultTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      phase_key,
      tier,
    }: {
      phase_key: string;
      tier: 'good' | 'better' | 'best';
    }) => {
      // First, set all tiers for this phase to non-default
      await supabase
        .from('phase_tier_defaults')
        .update({ is_default: false })
        .eq('phase_key', phase_key);

      // Then set the chosen tier as default
      const { data, error } = await supabase
        .from('phase_tier_defaults')
        .update({ is_default: true })
        .eq('phase_key', phase_key)
        .eq('tier', tier)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phase-tier-defaults'] });
      toast.success('Default tier updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update default tier', { description: error.message });
    },
  });
}

// Delete a tier default
export function useDeletePhaseTierDefault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('phase_tier_defaults')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phase-tier-defaults'] });
      toast.success('Tier default removed');
    },
  });
}
